import { Button, Container, Icon, NetworkIcon } from 'gu-ui-kit'
import { createPath } from 'history'
import pick from 'lodash/pick'
import qs from 'qs'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Link } from 'react-router-dom'
import { formatByLocale } from 'utils/formatMoney'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import prepareFilterParams from 'utils/prepareFilterParams'
import styles from 'modules/Statistics/Layout/Table/statistics-table.module.scss'
import NETWORK_PREFIX_MAP from 'modules/Statistics/networkPrefixMap'
import { ROUTES } from 'constants/routes'

const networkIconNamesMap = {
  yandexdirect: 'yandex',
  vk: 'vk',
  googleads: 'adv',
  vkads: 'vk-ads',
}

const useColumnConfig = (
  includeVat,
  tableData,
  summary,
  groupBy,
  callbacks = {},
) => {
  const pathGen = useGeneratePathI18n()
  const { sm: isMobile } = useBreakpoint()
  const { filters } = useSelector((state) => state['statistics/filters'])
  const { t } = useTranslation(['statistics'])
  const {
    i18n,
    user: { id: userId },
  } = useSelector(({ app }) => app)
  const sticky = isMobile ? false : 'left'

  return useMemo(() => {
    switch (groupBy) {
      case 'accounts':
        return [
          {
            id: 'account.network',
            Header: t('statistics::table.columns.header.network'),
            accessor: 'account.network',
            sticky,
            hideable: false,
            width: 66,
            Cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                // eslint-disable-next-line react/prop-types
                original: {
                  // eslint-disable-next-line react/prop-types
                  account: { network },
                },
              },
            }) => (
              <Container display="flex" className={styles['network-cell']}>
                {networkIconNamesMap[network] ? (
                  <NetworkIcon
                    name={networkIconNamesMap[network]}
                    size={'s'}
                    rounded
                  />
                ) : null}
              </Container>
            ),
          },
          {
            id: 'account.login',
            Header: t('statistics::table.columns.header.account'),
            accessor: 'account.login',
            sticky,
            hideable: false,
            minWidth: 255,
            Cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                // eslint-disable-next-line react/prop-types
                original: { account },
              },
              // eslint-disable-next-line react/prop-types
              value,
            }) => (
              <Container
                display="flex"
                className={styles['login-cell']}
                data-content={value}
              >
                <div className={'overflow'}>
                  {/* eslint-disable-next-line react/prop-types */}
                  {account.network !== 'vkads' &&
                  // eslint-disable-next-line react/prop-types
                  NETWORK_PREFIX_MAP[account.network] ? (
                    <Link
                      className={styles['navlink']}
                      to={createPath({
                        pathname: pathGen(ROUTES.STATISTICS_ACCOUNT, {
                          // eslint-disable-next-line react/prop-types
                          network: account.network,
                          // eslint-disable-next-line react/prop-types
                          externalId: account.id,
                        }),
                        search: qs.stringify(
                          prepareFilterParams(
                            pick(filters, [
                              'date_group_period',
                              'date_from',
                              'date_to',
                            ]),
                          ),
                          { arrayFormat: 'brackets' },
                        ),
                      })}
                    >
                      {value}
                    </Link>
                  ) : (
                    <span className={styles['login']}>{value}</span>
                  )}
                </div>
              </Container>
            ),
            Footer: () => <>{t('statistics::table.footer.summary')}:</>,
          },
          {
            id: 'account.active',
            Header: t('statistics::table.columns.header.status'),
            accessor: 'account.active',
            width: 80,
            hideable: false,
            Cell: (cellProps) =>
              cellProps.value === true ? (
                <Icon name={'status-on'} size={'s'} color={'secondary-10'} />
              ) : (
                <Icon
                  name={'status-archive'}
                  size={'s'}
                  color={'basic-secondary-grey'}
                />
              ),
          },
          {
            id: 'account.id',
            Header: t('statistics::table.columns.header.analytics'),
            accessor: 'account.id',
            width: 130,
            canSort: false,
            Cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                // eslint-disable-next-line react/prop-types
                original: {
                  // eslint-disable-next-line react/prop-types
                  account: {
                    // eslint-disable-next-line react/prop-types
                    network,
                    // eslint-disable-next-line react/prop-types
                    google_analytics_profile_name,
                    // eslint-disable-next-line react/prop-types
                    google_analytics_profile_id,
                  },
                },
              },
              // eslint-disable-next-line react/prop-types
              value,
            }) => {
              if (network !== 'yandexdirect') {
                return '—'
              }

              if (
                google_analytics_profile_name != null ||
                google_analytics_profile_id != null
              ) {
                return (
                  google_analytics_profile_name || google_analytics_profile_id
                )
              }

              return (
                <Button
                  onClick={(e) => {
                    callbacks.createConnectWindow &&
                      callbacks.createConnectWindow(e)
                  }}
                  href={`${
                    global.location.origin
                  }/api/app/v1/anl/google-analytics/oauth/connect?redirect_uri=${encodeURIComponent(
                    `${global.location.origin}/statistics/accounts/connect?adv_type=${network}&adv_account_id=${value}`,
                  )}`}
                  variant={'outlined'}
                  size={'xs'}
                  color={'Label-GU-Primary'}
                  component={'a'}
                >
                  {t('analytics.connect')}
                </Button>
              )
            },
            Footer: () => <></>,
          },
          {
            id: 'impressions',
            Header: t('statistics::table.columns.header.impressions'),
            accessor: 'impressions',
            minWidth: 120,
            width: 120,
            Cell: (cellProps) =>
              cellProps.value === null ? '—' : formatByLocale(cellProps.value),
            Footer: () => formatByLocale(summary.impressions),
          },
          {
            id: 'cost',
            Header: t(
              'table.columns.header.' + (includeVat ? 'cost_with_vat' : 'cost'),
            ),
            accessor: includeVat ? 'cost_with_vat' : 'cost',
            minWidth: 150,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              formatByLocale(
                summary[includeVat ? 'cost_with_vat' : 'cost'],
                i18n,
                { maximumFractionDigits: 2 },
              ),
          },
          {
            id: 'revenue',
            Header: t('statistics::table.columns.header.revenue'),
            accessor: 'revenue',
            minWidth: 150,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              formatByLocale(summary.revenue, i18n, {
                maximumFractionDigits: 2,
              }),
          },
          {
            id: 'clicks',
            Header: t('statistics::table.columns.header.clicks'),
            accessor: 'clicks',
            minWidth: 100,
            width: 120,
            Cell: (cellProps) =>
              cellProps.value === null ? '—' : formatByLocale(cellProps.value),
            Footer: () => formatByLocale(summary.clicks),
          },
          {
            id: 'ctr',
            Header: t('statistics::table.columns.header.ctr'),
            accessor: 'ctr',
            minWidth: 80,
            width: 100,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              formatByLocale(summary.ctr, i18n, { maximumFractionDigits: 2 }),
          },
          {
            id: 'avg_click_price',
            Header: t('statistics::table.columns.header.avg_click_price'),
            accessor: 'avg_click_price',
            minWidth: 180,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              formatByLocale(summary.avg_click_price, i18n, {
                maximumFractionDigits: 2,
              }),
          },
          {
            id: 'conversions',
            Header: t('statistics::table.columns.header.conversions'),
            accessor: 'conversions',
            minWidth: 100,
            Cell: (cellProps) =>
              cellProps.value === null ? '—' : formatByLocale(cellProps.value),
            Footer: () => formatByLocale(summary.conversions),
          },
          {
            id: 'conversion_rate',
            Header: t('statistics::table.columns.header.conversion_rate'),
            accessor: 'conversion_rate',
            minWidth: 150,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              formatByLocale(summary.conversion_rate, i18n, {
                maximumFractionDigits: 2,
              }),
          },
        ]

      case 'networks':
        return [
          {
            id: 'network',
            Header: t('statistics::table.columns.header.network'),
            accessor: 'network',
            sticky,
            width: 75,
            Cell: (cellProps) => (
              <Container display="flex" className={styles['network-cell']}>
                {networkIconNamesMap[cellProps.value] ? (
                  <NetworkIcon
                    name={networkIconNamesMap[cellProps.value]}
                    size={'s'}
                    rounded
                  />
                ) : null}
              </Container>
            ),
            Footer: () => <>{t('statistics::table.footer.summary')}:</>,
          },
          {
            id: 'impressions',
            Header: t('statistics::table.columns.header.impressions'),
            accessor: 'impressions',
            minWidth: 120,
            width: 160,
            Cell: (cellProps) =>
              cellProps.value === null ? '—' : formatByLocale(cellProps.value),
            Footer: () => formatByLocale(summary.impressions),
          },
          {
            id: 'cost',
            Header: t(
              'table.columns.header.' + (includeVat ? 'cost_with_vat' : 'cost'),
            ),
            accessor: includeVat ? 'cost_with_vat' : 'cost',
            minWidth: 180,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              formatByLocale(
                summary[includeVat ? 'cost_with_vat' : 'cost'],
                i18n,
                { maximumFractionDigits: 2 },
              ),
          },
          {
            id: 'revenue',
            Header: t('statistics::table.columns.header.revenue'),
            accessor: 'revenue',
            minWidth: 160,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              formatByLocale(summary.revenue, i18n, {
                maximumFractionDigits: 2,
              }),
          },
          {
            id: 'clicks',
            Header: t('statistics::table.columns.header.clicks'),
            accessor: 'clicks',
            minWidth: 100,
            Cell: (cellProps) =>
              cellProps.value === null ? '—' : formatByLocale(cellProps.value),
            Footer: () => formatByLocale(summary.clicks),
          },
          {
            id: 'ctr',
            Header: t('statistics::table.columns.header.ctr'),
            accessor: 'ctr',
            minWidth: 80,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              formatByLocale(summary.ctr, i18n, { maximumFractionDigits: 2 }),
          },
          {
            id: 'avg_click_price',
            Header: t('statistics::table.columns.header.avg_click_price'),
            accessor: 'avg_click_price',
            minWidth: 190,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              formatByLocale(summary.avg_click_price, i18n, {
                maximumFractionDigits: 2,
              }),
          },
          {
            id: 'conversions',
            Header: t('statistics::table.columns.header.conversions'),
            accessor: 'conversions',
            minWidth: 100,
            Cell: (cellProps) =>
              cellProps.value === null ? '—' : formatByLocale(cellProps.value),
            Footer: () => formatByLocale(summary.conversions),
          },
          {
            id: 'conversion_rate',
            Header: t('statistics::table.columns.header.conversion_rate'),
            accessor: 'conversion_rate',
            minWidth: 200,
            Cell: (cellProps) =>
              cellProps.value === null
                ? '—'
                : formatByLocale(cellProps.value, i18n, {
                    maximumFractionDigits: 2,
                  }),
            Footer: () =>
              formatByLocale(summary.conversion_rate, i18n, {
                maximumFractionDigits: 2,
              }),
          },
        ]
    }
  }, [includeVat, i18n, tableData, userId])
}

export default useColumnConfig
